import { Fragment } from 'react';

import { Line, ReferenceDot } from 'recharts';

import { GraphLineNode } from '@data';

type GraphPoints = Record<
    string,
    {
        frame: number;
        value: number;
    }
>;

type RenderLineParams = {
    lines: GraphLineNode[] | undefined;
    filteredDataKeys?: string[];
    colorMap: Record<string, string>;
    displaySymbol: string;
    showComparisonData: boolean;
    shouldHideComparison: boolean;
    apex: GraphPoints;
    transition: GraphPoints;
    translate: (key: string) => string;
};

export const renderLines = ({
    lines,
    filteredDataKeys = [],
    colorMap,
    displaySymbol,
    apex,
    showComparisonData,
    shouldHideComparison,
    transition,
    translate,
}: RenderLineParams) => {
    return lines?.map((line) => {
        const axis = line?.axis || 'x';
        const lineIsHidden = filteredDataKeys.includes(line.id as string);

        const color = colorMap[line.id];
        const name = translate(line.i18n ?? '-');

        const thickness = 2;

        return (
            <Fragment key={line.id}>
                {/* Current line */}
                <Line
                    key={`line_${line.id}_current`}
                    dataKey={(dataPoint) => dataPoint[line.id]?.current}
                    yAxisId={displaySymbol}
                    stroke={color}
                    strokeWidth={thickness}
                    name={`${name}`}
                    activeDot={false}
                    dot={false}
                    isAnimationActive={false}
                    type="linear"
                    connectNulls
                    hide={lineIsHidden}
                />

                {/* Comparison line */}
                {showComparisonData && !shouldHideComparison && (
                    <Line
                        key={`line_${line.id}_comparison`}
                        dataKey={(dataPoint) => dataPoint[line.id]?.comparison}
                        yAxisId={displaySymbol}
                        stroke={color}
                        strokeWidth={thickness / 2}
                        strokeDasharray="5 5"
                        name={`${name} (Comparison)`}
                        opacity={0.8}
                        activeDot={false}
                        dot={false}
                        isAnimationActive={false}
                        type="linear"
                        connectNulls
                        hide={lineIsHidden}
                    />
                )}
                {/* render Apex dots */}
                {!lineIsHidden && apex[line.id] && (
                    <ReferenceDot
                        x={apex[line.id].frame}
                        y={apex[line.id].value}
                        r={6}
                        fill="white"
                        strokeWidth={2}
                        stroke={color}
                        yAxisId={displaySymbol}
                    />
                )}

                {/* render Transition dots */}
                {!lineIsHidden && transition[line.id] && (
                    <ReferenceDot
                        x={transition[line.id].frame}
                        y={transition[line.id].value}
                        r={6}
                        fill="white"
                        strokeWidth={2}
                        stroke={color}
                        yAxisId={displaySymbol}
                    />
                )}
            </Fragment>
        );
    });
};
